import { useEffect, useMemo, useState, useCallback, useRef, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  Skeleton,
  LinearProgress,
  Tooltip as MuiTooltip,
  Grid,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Button,
  ButtonBase,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { formatDistanceToNow, format } from 'date-fns';
import { useMetricsStore } from '../../stores/metricsStore';
import { useExclusionsStore } from '../../stores/exclusionsStore';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { getOrgHealth, getOrgList, updateOrgCompanySize } from '../../api/dashboard';
import HealthSettingsDialog from './HealthSettingsDialog';
import OrgHealthDrawer from './OrgHealthDrawer';
import { HEALTH_TIER_STYLES } from './healthTierStyles';
import { formatOrgDisplayName } from '../../utils/display';
import type { OrgHealthScore, OrgListEntry, OrgPlan, OrgCompanySize } from '../../types';

const PLAN_STYLES: Record<OrgPlan, { label: string; color: string; bg: string }> = {
  enterprise: { label: 'Enterprise', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)' },
  pro: { label: 'Pro', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
  lite: { label: 'Lite', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.15)' },
  none: { label: '—', color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)' },
};

const COMPANY_SIZE_LABELS: Record<OrgCompanySize, string> = {
  small: 'Small',
  mid_size: 'Mid-size',
  large: 'Large',
  very_large: 'Very large',
};

// Map activity_log (event_category, event_type) to a friendly verb phrase.
// Falls back to a generic "did {category}/{event_type}" when the pair isn't recognized.
const EVENT_PHRASES: Record<string, string> = {
  'workflow/run': 'ran a workflow',
  'workflow/plan': 'planned a workflow',
  'query/create': 'asked a query',
  'query/answer': 'answered a query',
  'kb_chat/create': 'chatted with the knowledge base',
  'project/create': 'created a project',
  'project/view': 'viewed a project',
  'project/edit': 'edited a project',
  'compare/create': 'ran a compare',
  'document/create': 'added a document',
  'document/upload': 'uploaded a document',
  'checklist/create': 'created a checklist',
  'checklist/run': 'ran a checklist',
  'page/create': 'ingested a page',
  'page/view': 'viewed a page',
};

function describeEvent(category: string | null, eventType: string | null): string {
  if (!category && !eventType) return 'was active';
  const key = `${category ?? ''}/${eventType ?? ''}`;
  if (EVENT_PHRASES[key]) return EVENT_PHRASES[key];
  if (category && eventType) return `did ${category} · ${eventType}`;
  return category || eventType || 'was active';
}

const COMPANY_SIZE_OPTIONS: Array<{ value: OrgCompanySize | null; label: string; sub: string }> = [
  { value: 'small', label: 'Small', sub: '1–50 employees' },
  { value: 'mid_size', label: 'Mid-size', sub: '51–200 employees' },
  { value: 'large', label: 'Large', sub: '201–1,000 employees' },
  { value: 'very_large', label: 'Very large', sub: '1,000+ employees' },
  { value: null, label: 'Not set', sub: 'Clear value' },
];

interface CompanySizeEditorProps {
  orgId: string;
  value: OrgCompanySize | null;
  onChange: (next: OrgCompanySize | null) => Promise<void>;
}

function CompanySizeEditor({ orgId, value, onChange }: CompanySizeEditorProps) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [busy, setBusy] = useState(false);

  const open = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation(); // don't trigger the row-click navigation
    setAnchor(e.currentTarget);
  };
  const close = () => setAnchor(null);

  const handleSelect = async (next: OrgCompanySize | null) => {
    close();
    if (next === value) return;
    setBusy(true);
    try {
      await onChange(next);
    } finally {
      setBusy(false);
    }
  };

  const label = value ? COMPANY_SIZE_LABELS[value] : 'Set size';

  return (
    <>
      <Box
        onClick={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          cursor: busy ? 'wait' : 'pointer',
          px: 0.5,
          ml: -0.5,
          py: 0.1,
          borderRadius: 0.75,
          opacity: busy ? 0.6 : 1,
          transition: 'background-color 100ms ease',
          '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.12)' },
        }}
      >
        <Typography
          variant="caption"
          color={value ? 'text.secondary' : 'text.disabled'}
          sx={{ fontSize: '0.7rem', lineHeight: 1.1 }}
        >
          {label}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              bgcolor: '#1e293b',
              border: '1px solid rgba(148, 163, 184, 0.12)',
              minWidth: 200,
            },
          },
        }}
      >
        {COMPANY_SIZE_OPTIONS.map((opt) => {
          const selected = opt.value === value;
          return (
            <MenuItem
              key={opt.value ?? '__none'}
              onClick={() => handleSelect(opt.value)}
              data-org-id={orgId}
              sx={{ py: 1, gap: 1.5 }}
            >
              <Box sx={{ width: 16, display: 'flex', justifyContent: 'center' }}>
                {selected && <CheckIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />}
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400, lineHeight: 1.2 }}>
                  {opt.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {opt.sub}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

interface SummaryStripProps {
  orgs: OrgListEntry[];
}

function SummaryStrip({ orgs }: SummaryStripProps) {
  const stats = useMemo(() => {
    const total = orgs.length;
    const active = orgs.filter((o) => o.activeUsersInRange > 0).length;
    const totalUsers = orgs.reduce((sum, o) => sum + o.totalUsers, 0);
    const activeUsers = orgs.reduce((sum, o) => sum + o.activeUsersInRange, 0);
    const queries = orgs.reduce((sum, o) => sum + o.queriesInRange, 0);
    const workflows = orgs.reduce((sum, o) => sum + o.workflowsInRange, 0);
    return { total, active, totalUsers, activeUsers, queries, workflows };
  }, [orgs]);

  const cards: Array<{
    title: string;
    value: string;
    subtitle: string;
    color: string;
    tooltip: string;
  }> = [
    {
      title: 'Total Orgs',
      value: stats.total.toLocaleString(),
      subtitle: 'In view',
      color: '#8b5cf6',
      tooltip: 'Total active organizations after applying exclusions.',
    },
    {
      title: 'Active in range',
      value: stats.active.toLocaleString(),
      subtitle: `${stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% of total`,
      color: '#10b981',
      tooltip: 'Organizations with at least one user active during the selected date range.',
    },
    {
      title: 'Idle in range',
      value: (stats.total - stats.active).toLocaleString(),
      subtitle: 'No activity',
      color: '#f59e0b',
      tooltip: 'Organizations with no activity during the selected date range.',
    },
    {
      title: 'Registered users',
      value: stats.totalUsers.toLocaleString(),
      subtitle: 'Across all orgs',
      color: '#06b6d4',
      tooltip: 'Sum of active registered users across all organizations.',
    },
    {
      title: 'Active users',
      value: stats.activeUsers.toLocaleString(),
      subtitle: 'In range',
      color: '#a78bfa',
      tooltip: 'Total distinct users with activity in the selected range, summed across orgs.',
    },
    {
      title: 'Q + W',
      value: (stats.queries + stats.workflows).toLocaleString(),
      subtitle: `${stats.queries.toLocaleString()} Q · ${stats.workflows.toLocaleString()} W`,
      color: '#22c55e',
      tooltip: 'Total queries and workflows executed across all orgs in the selected range.',
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card) => (
        <Grid item xs={6} sm={4} md={2} key={card.title}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">
                  {card.title}
                </Typography>
                <MuiTooltip
                  title={card.tooltip}
                  arrow
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: { bgcolor: '#1e293b', fontSize: '0.75rem', maxWidth: 280, p: 1.5 },
                    },
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      cursor: 'help',
                      '&:hover': { color: 'primary.main' },
                    }}
                  />
                </MuiTooltip>
              </Box>
              <Typography variant="h4" fontWeight={600} sx={{ color: card.color }}>
                {card.value}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.subtitle}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

/** Score change vs the previous window; nothing under a point, which is noise. */
function ScoreTrend({ score, previousScore }: { score: number; previousScore: number }) {
  const diff = Math.round(score - previousScore);
  if (diff === 0) return null;
  const up = diff > 0;
  const Icon = up ? ArrowDropUpIcon : ArrowDropDownIcon;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', color: up ? '#22c55e' : '#ef4444', ml: -0.5 }}>
      <Icon sx={{ fontSize: 18, mr: -0.5 }} />
      <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {Math.abs(diff)}
      </Typography>
    </Box>
  );
}

export default function OrganizationsView() {
  const navigate = useNavigate();
  const {
    orgList,
    isOrgListLoading,
    dateRange,
    selectedOrgIds,
    setOrgList,
    setOrgListLoading,
  } = useMetricsStore();
  const { excludedOrgIds, effectiveExcludedUserIds } = useExclusionsStore();
  const setOnboardingOrgIds = useOnboardingStore((state) => state.setSelectedOrgIds);

  const [search, setSearch] = useState('');
  // Health lives in local state, not the metrics store: only this table reads it, and it
  // is the one column here that ignores the date range.
  const [health, setHealth] = useState<Map<string, OrgHealthScore>>(new Map());
  // Whether `health` holds a successful fetch: an empty map is also what loading and failure look like.
  const [healthLoaded, setHealthLoaded] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The org whose health breakdown drawer is open.
  const [healthOrgId, setHealthOrgId] = useState<string | null>(null);
  const isFetchingRef = useRef(false);

  const handleCompanySizeChange = useCallback(
    async (orgId: string, next: OrgCompanySize | null) => {
      // Capture only this row's previous size — concurrent edits on other rows must not be clobbered.
      let previousValue: OrgCompanySize | null = null;
      setOrgList((prev) =>
        prev.map((o) => {
          if (o.orgId !== orgId) return o;
          previousValue = o.companySize;
          return { ...o, companySize: next };
        }),
      );
      try {
        await updateOrgCompanySize(orgId, next);
      } catch (err) {
        console.error('Failed to update company size:', err);
        setOrgList((prev) =>
          prev.map((o) => (o.orgId === orgId ? { ...o, companySize: previousValue } : o)),
        );
        setErrorMsg('Failed to update company size. The change was reverted — please try again.');
      }
    },
    [setOrgList],
  );

  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchOrgs = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setOrgListLoading(true);
    try {
      const data = await getOrgList(
        dateRange.start,
        dateRange.end,
        excludedOrgIds.length > 0 ? excludedOrgIds : undefined,
        effectiveExcludedUserIds.length > 0 ? effectiveExcludedUserIds : undefined,
      );
      setOrgList(data);
      setFetchError(null);
    } catch (error) {
      console.error('Failed to fetch org list:', error);
      setFetchError(
        error instanceof Error
          ? `Couldn't load organizations: ${error.message}`
          : "Couldn't load organizations.",
      );
    } finally {
      setOrgListLoading(false);
      isFetchingRef.current = false;
    }
  }, [dateRange, excludedOrgIds, effectiveExcludedUserIds, setOrgList, setOrgListLoading]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // Health is fetched separately from the org list on purpose. It is a fixed 28-day window,
  // so it cannot change with the date-range picker — folding it into `fetchOrgs` would
  // re-run the page's heaviest query on every 7D/30D/90D click for identical results, and
  // would hold `isFetchingRef` long enough that a range change during the fetch gets
  // dropped and never retried.
  const fetchHealth = useCallback(async () => {
    try {
      const data = await getOrgHealth(
        excludedOrgIds.length > 0 ? excludedOrgIds : undefined,
        effectiveExcludedUserIds.length > 0 ? effectiveExcludedUserIds : undefined,
      );
      setHealth(new Map(data.map((h) => [h.orgId, h])));
      setHealthLoaded(true);
    } catch (error) {
      // Non-fatal: the Health column renders "—" and the rest of the table still works.
      console.error('Failed to fetch org health:', error);
      setHealth(new Map());
      setHealthLoaded(false);
    }
  }, [excludedOrgIds, effectiveExcludedUserIds]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const healthRows = useMemo(() => Array.from(health.values()), [health]);

  const filteredOrgs = useMemo(() => {
    let rows = orgList;
    if (selectedOrgIds.length > 0) {
      const selected = new Set(selectedOrgIds);
      rows = rows.filter((o) => selected.has(o.orgId));
    }
    if (search.trim()) {
      const needle = search.toLowerCase().trim();
      rows = rows.filter((o) => o.name.toLowerCase().includes(needle));
    }
    return rows;
  }, [orgList, selectedOrgIds, search]);

  const handleRowClick = useCallback(
    (orgId: string) => {
      setSelectedRowId((prev) => (prev === orgId ? null : orgId));
    },
    [],
  );

  const handleViewUsers = useCallback(
    (orgId: string) => {
      setOnboardingOrgIds([orgId]);
      navigate('/onboarding');
    },
    [navigate, setOnboardingOrgIds],
  );

  const columns: GridColDef<OrgListEntry>[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Organization',
      flex: 1.6,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<OrgListEntry>) => {
        const plan = PLAN_STYLES[params.row.plan];
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%', minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ color: 'text.primary', lineHeight: 1.3, flexShrink: 1, minWidth: 0 }}
            >
              {formatOrgDisplayName(params.row.name)}
            </Typography>
            <Chip
              label={plan.label}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: plan.color,
                bgcolor: plan.bg,
                border: 'none',
                flexShrink: 0,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <CompanySizeEditor
              orgId={params.row.orgId}
              value={params.row.companySize}
              onChange={(next) => handleCompanySizeChange(params.row.orgId, next)}
            />
          </Box>
        );
      },
    },
    {
      field: 'health',
      headerName: 'Health',
      width: 190,
      // Sorting has to run off the number, not the rendered chip. `null` (rather than a
      // low sentinel like -1) keeps unscored orgs at the bottom in BOTH directions —
      // -1 would float every "—" row to the top of an ascending sort, burying the
      // genuinely at-risk orgs the sort exists to surface.
      valueGetter: (_value, row: OrgListEntry) => health.get(row.orgId)?.score ?? null,
      renderCell: (params: GridRenderCellParams<OrgListEntry>) => {
        const h = health.get(params.row.orgId);
        if (!h) {
          return (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          );
        }
        const style = HEALTH_TIER_STYLES[h.tier];
        return (
          <MuiTooltip
            arrow
            placement="right"
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  {style.label} · {h.score.toFixed(1)} / 100
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Activation {h.activation.toFixed(0)} — {h.activeUsers} of {h.paidSeats} pro seats active
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Habit {h.habit.toFixed(0)} — {h.avgActiveDaysPerUser.toFixed(1)} active days per user
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Depth {h.depth.toFixed(0)} — {h.requests.toLocaleString()} requests
                </Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Reliability {h.reliability.toFixed(0)} — {h.failedRequests.toLocaleString()} failed
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  Previous {h.windowDays} days: {h.previousScore.toFixed(1)} ({HEALTH_TIER_STYLES[h.previousTier].label})
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
                >
                  Rolling {h.windowDays} days, pro seats only — not the selected range. Click for why.
                </Typography>
              </Box>
            }
          >
            <ButtonBase
              onClick={(e) => {
                // A row click toggles the row's actions; this opens the breakdown instead.
                e.stopPropagation();
                setHealthOrgId(params.row.orgId);
              }}
              aria-label={`Why ${params.row.name} scores ${h.score.toFixed(0)}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                border: '1px solid rgba(148, 163, 184, 0.18)',
                transition: 'background-color 120ms ease, border-color 120ms ease',
                '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.12)', borderColor: '#8b5cf6' },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: style.color }}
              >
                {h.score.toFixed(0)}
              </Typography>
              <Chip
                label={style.label}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: style.color,
                  bgcolor: style.bg,
                  border: 'none',
                  cursor: 'inherit',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <ScoreTrend score={h.score} previousScore={h.previousScore} />
              <InsightsOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </ButtonBase>
          </MuiTooltip>
        );
      },
    },
    {
      field: 'totalUsers',
      headerName: 'Users',
      type: 'number',
      width: 100,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <MuiTooltip
          title={`${params.row.paidUsers.toLocaleString()} paid (non-lite) users`}
          arrow
          placement="top"
        >
          <Typography variant="body2" sx={{ cursor: 'help', fontVariantNumeric: 'tabular-nums' }}>
            {params.row.totalUsers.toLocaleString()}
          </Typography>
        </MuiTooltip>
      ),
    },
    {
      field: 'recentActiveUsers',
      headerName: 'Active 30d',
      type: 'number',
      width: 130,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const pct = params.row.totalUsers > 0
          ? Math.round((params.row.recentActiveUsers / params.row.totalUsers) * 100)
          : 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {params.row.recentActiveUsers.toLocaleString()}
            </Typography>
            {params.row.totalUsers > 0 && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  px: 0.6,
                  borderRadius: 0.5,
                  bgcolor: pct >= 50 ? 'rgba(34, 197, 94, 0.15)' : pct >= 20 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(148, 163, 184, 0.12)',
                  color: pct >= 50 ? '#22c55e' : pct >= 20 ? '#f59e0b' : '#94a3b8',
                  fontWeight: 600,
                }}
              >
                {pct}%
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'activeUsersInRange',
      headerName: 'Active in range',
      type: 'number',
      width: 140,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const v = params.row.activeUsersInRange;
        const color = v === 0 ? '#64748b' : v >= 5 ? '#22c55e' : v >= 1 ? '#f59e0b' : '#94a3b8';
        return (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color, fontWeight: v > 0 ? 600 : 400 }}>
            {v.toLocaleString()}
          </Typography>
        );
      },
    },
    {
      field: 'queriesInRange',
      headerName: 'Queries',
      type: 'number',
      width: 100,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', color: params.row.queriesInRange > 0 ? 'text.primary' : 'text.disabled' }}
        >
          {params.row.queriesInRange.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'workflowsInRange',
      headerName: 'Workflows',
      type: 'number',
      width: 110,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', color: params.row.workflowsInRange > 0 ? 'text.primary' : 'text.disabled' }}
        >
          {params.row.workflowsInRange.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'lastActivity',
      headerName: 'Last activity',
      width: 140,
      sortComparator: (a, b) => {
        const aTime = a ? new Date(a as string).getTime() : 0;
        const bTime = b ? new Date(b as string).getTime() : 0;
        return aTime - bTime;
      },
      renderCell: (params) => {
        if (!params.row.lastActivity) {
          return (
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              &gt; 90 days
            </Typography>
          );
        }
        const date = new Date(params.row.lastActivity);
        const actor = params.row.lastActorName || 'Someone';
        const verb = describeEvent(params.row.lastEventCategory, params.row.lastEventType);
        const tooltipNode = (
          <Box sx={{ p: 0.25, lineHeight: 1.45 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#f8fafc', fontSize: '0.78rem' }}>
              {actor}
            </Typography>
            <Typography variant="caption" sx={{ color: '#cbd5e1', display: 'block', fontSize: '0.72rem' }}>
              {verb}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.25, fontSize: '0.68rem' }}>
              {format(date, 'PPpp')}
            </Typography>
          </Box>
        );
        return (
          <MuiTooltip
            title={tooltipNode}
            arrow
            placement="top"
            slotProps={{
              tooltip: { sx: { bgcolor: '#1e293b', maxWidth: 260, p: 1.25 } },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ cursor: 'help' }}>
              {formatDistanceToNow(date, { addSuffix: true })}
            </Typography>
          </MuiTooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const isSelected = selectedRowId === params.row.orgId;
        if (!isSelected) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<PeopleAltOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewUsers(params.row.orgId);
              }}
              sx={{
                fontSize: '0.7rem',
                py: 0.25,
                px: 1,
                minWidth: 0,
                bgcolor: '#8b5cf6',
                boxShadow: '0 2px 8px rgba(139, 92, 246, 0.35)',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: '#7c3aed' },
              }}
            >
              View users
            </Button>
          </Box>
        );
      },
    },
  ], [health, selectedRowId, handleCompanySizeChange, handleViewUsers]);

  if (isOrgListLoading && orgList.length === 0) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={6} sm={4} md={2} key={i}>
              <Skeleton variant="rounded" height={110} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={500} />
      </Box>
    );
  }

  return (
    <Box>
      {isOrgListLoading && orgList.length > 0 && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200, height: 3 }} />
      )}

      <SummaryStrip orgs={filteredOrgs} />

      {fetchError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchOrgs}>
              Retry
            </Button>
          }
        >
          {fetchError}
        </Alert>
      )}

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder="Search organizations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              Showing <strong>{filteredOrgs.length}</strong> of <strong>{orgList.length}</strong> orgs · click a row to reveal actions
            </Typography>
            <Button
              size="small"
              startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
              onClick={() => setSettingsOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Health settings
            </Button>
          </Box>
        </Box>

        <DataGrid
          rows={filteredOrgs}
          columns={columns}
          getRowId={(row) => row.orgId}
          getRowClassName={(params) => (selectedRowId === params.row.orgId ? 'org-row--selected' : '')}
          onRowClick={(params) => handleRowClick(params.row.orgId)}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={56}
          initialState={{
            sorting: { sortModel: [{ field: 'activeUsersInRange', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'rgba(15, 23, 42, 0.6)',
              borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              transition: 'background-color 120ms ease',
            },
            '& .MuiDataGrid-row:hover': {
              bgcolor: 'rgba(139, 92, 246, 0.06)',
            },
            '& .MuiDataGrid-row.org-row--selected': {
              bgcolor: 'rgba(139, 92, 246, 0.16)',
              boxShadow: 'inset 2px 0 0 #8b5cf6',
            },
            '& .MuiDataGrid-row.org-row--selected:hover': {
              bgcolor: 'rgba(139, 92, 246, 0.22)',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(148, 163, 184, 0.06)',
              outline: 'none !important',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(148, 163, 184, 0.1)',
            },
          }}
        />
      </Paper>

      <HealthSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={fetchHealth}
        currentHealth={healthLoaded ? healthRows : null}
        excludedOrgIds={excludedOrgIds}
        excludedUserIds={effectiveExcludedUserIds}
      />

      <OrgHealthDrawer
        orgId={healthOrgId}
        onClose={() => setHealthOrgId(null)}
        excludedOrgIds={excludedOrgIds}
        excludedUserIds={effectiveExcludedUserIds}
      />

      <Snackbar
        open={Boolean(errorMsg)}
        onClose={(_, reason) => {
          // Don't auto-dismiss on outside click — write failures must stay visible until acknowledged.
          if (reason === 'clickaway') return;
          setErrorMsg(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
